import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { Plus, Trash2, Send, Save, CheckCircle2 } from "lucide-react";
import {
  listMyTemplates, saveTemplate, deleteTemplate,
  sendAssessment, listRecruiterAssignments, gradeAssignment,
} from "@/lib/api/lxp-assessments.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/recruiter/assessments")({
  head: () => ({ meta: [{ title: "Custom Assessments | SA LexPractiq Recruiter" }] }),
  component: RecruiterAssessments,
});

type Question = {
  id: string;
  type: "mcq" | "short" | "long" | "boolean";
  prompt: string;
  options?: string[];
  answer?: string | boolean;
  points: number;
};

const emptyQuestion = (): Question => ({
  id: crypto.randomUUID().slice(0, 8),
  type: "short",
  prompt: "",
  points: 10,
});

function RecruiterAssessments() {
  const list = useServerFn(listMyTemplates);
  const save = useServerFn(saveTemplate);
  const del = useServerFn(deleteTemplate);
  const send = useServerFn(sendAssessment);
  const listA = useServerFn(listRecruiterAssignments);
  const grade = useServerFn(gradeAssignment);

  const [templates, setTemplates] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [sending, setSending] = useState<any | null>(null);
  const [grading, setGrading] = useState<any | null>(null);

  async function refresh() {
    try {
      const [t, a] = await Promise.all([list(), listA()]);
      setTemplates((t as any).items); setAssignments((a as any).items);
    } catch (e: any) { toast.error(e?.message || "Failed to load"); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <h1 style={{ margin: 0 }}>Custom Assessments</h1>
            <p className="lxp-muted" style={{ margin: "0.25rem 0 0" }}>Build assessment templates, send them to candidates, and review submissions.</p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Link className="lxp-btn-ghost" to="/lexpractiq/recruiter">Back to search</Link>
            <button className="lxp-btn" onClick={() => setEditing({ title: "", passingScore: 60, questions: [emptyQuestion()] })}>
              <Plus size={14} /> New template
            </button>
          </div>
        </div>

        <section style={{ marginTop: "1.5rem" }}>
          <h2 style={{ fontSize: "1.1rem", margin: "0 0 0.75rem" }}>Your templates</h2>
          <div style={{ display: "grid", gap: "0.75rem" }}>
            {templates.map((t) => (
              <div key={t.id} className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                <div>
                  <h3 style={{ margin: "0 0 0.2rem", fontSize: "0.95rem" }}>{t.title}</h3>
                  <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>
                    {t.questions?.length || 0} questions · Pass ≥ {t.passing_score}%{t.time_limit_min ? ` · ${t.time_limit_min} min` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.4rem" }}>
                  <button className="lxp-btn-ghost" onClick={() => setEditing({
                    id: t.id, title: t.title, description: t.description, instructions: t.instructions,
                    simulationId: t.simulation_id, timeLimitMin: t.time_limit_min,
                    passingScore: t.passing_score, questions: t.questions,
                  })}>Edit</button>
                  <button className="lxp-btn" onClick={() => setSending({ templateId: t.id, title: t.title })}>
                    <Send size={14} /> Send
                  </button>
                  <button className="lxp-btn-ghost" onClick={async () => {
                    if (!confirm("Delete this template?")) return;
                    try { await del({ data: { id: t.id } }); toast.success("Deleted"); refresh(); }
                    catch (e: any) { toast.error(e?.message); }
                  }} aria-label="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {templates.length === 0 && <p className="lxp-muted">No templates yet. Start by creating one.</p>}
          </div>
        </section>

        <section style={{ marginTop: "2rem" }}>
          <h2 style={{ fontSize: "1.1rem", margin: "0 0 0.75rem" }}>Sent assessments</h2>
          <div style={{ display: "grid", gap: "0.6rem" }}>
            {assignments.map((a) => (
              <div key={a.id} className="lxp-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                <div>
                  <span className="lxp-badge">{a.status}</span>
                  <h3 style={{ margin: "0.3rem 0 0.15rem", fontSize: "0.9rem" }}>{a.template?.title || "Assessment"}</h3>
                  <div className="lxp-muted" style={{ fontSize: "0.78rem" }}>
                    Candidate {a.candidate_id.slice(0, 8)}… · Sent {new Date(a.created_at).toLocaleDateString()}
                    {a.due_at ? ` · Due ${new Date(a.due_at).toLocaleDateString()}` : ""}
                    {a.score != null ? ` · Score ${a.score}%` : ""}
                  </div>
                </div>
                {a.status === "submitted" && (
                  <button className="lxp-btn" onClick={() => setGrading({ id: a.id, score: 70, feedback: "" })}>Grade</button>
                )}
              </div>
            ))}
            {assignments.length === 0 && <p className="lxp-muted">No assessments sent yet.</p>}
          </div>
        </section>

        {editing && (
          <TemplateEditor
            initial={editing}
            onClose={() => setEditing(null)}
            onSave={async (payload) => {
              try { await save({ data: payload }); toast.success("Saved"); setEditing(null); refresh(); }
              catch (e: any) { toast.error(e?.message); }
            }}
          />
        )}

        {sending && (
          <SendDialog
            templateTitle={sending.title}
            onCancel={() => setSending(null)}
            onSend={async ({ candidateId, message, dueAt }) => {
              try {
                await send({ data: { templateId: sending.templateId, candidateId, message, dueAt: dueAt || undefined } });
                toast.success("Assessment sent"); setSending(null); refresh();
              } catch (e: any) { toast.error(e?.message); }
            }}
          />
        )}

        {grading && (
          <GradeDialog
            initial={grading}
            onCancel={() => setGrading(null)}
            onSave={async ({ score, feedback }) => {
              try { await grade({ data: { assignmentId: grading.id, score, feedback } });
                toast.success("Graded"); setGrading(null); refresh();
              } catch (e: any) { toast.error(e?.message); }
            }}
          />
        )}
      </div>
    </LxpLayout>
  );
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "grid", placeItems: "center", padding: "1rem", zIndex: 50 }} onClick={onClose}>
      <div className="lxp-card" style={{ maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "auto", background: "var(--lxp-surface)" }} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function TemplateEditor({ initial, onSave, onClose }: { initial: any; onSave: (p: any) => void; onClose: () => void }) {
  const [state, setState] = useState<any>(initial);
  const set = (k: string, v: any) => setState((s: any) => ({ ...s, [k]: v }));
  const setQ = (i: number, patch: any) => setState((s: any) => {
    const q = [...s.questions]; q[i] = { ...q[i], ...patch }; return { ...s, questions: q };
  });

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: "0 0 1rem" }}>{state.id ? "Edit" : "New"} assessment template</h3>
      <label style={{ display: "block", marginBottom: "0.6rem" }}>
        <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Title</div>
        <input className="lxp-input" value={state.title} onChange={(e) => set("title", e.target.value)} />
      </label>
      <label style={{ display: "block", marginBottom: "0.6rem" }}>
        <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Description (shown to candidate)</div>
        <textarea className="lxp-textarea" rows={2} value={state.description || ""} onChange={(e) => set("description", e.target.value)} />
      </label>
      <label style={{ display: "block", marginBottom: "0.6rem" }}>
        <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Instructions</div>
        <textarea className="lxp-textarea" rows={3} value={state.instructions || ""} onChange={(e) => set("instructions", e.target.value)} />
      </label>
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
        <label style={{ flex: 1 }}>
          <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Time limit (min)</div>
          <input className="lxp-input" type="number" value={state.timeLimitMin || ""} onChange={(e) => set("timeLimitMin", e.target.value ? Number(e.target.value) : null)} />
        </label>
        <label style={{ flex: 1 }}>
          <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Passing score %</div>
          <input className="lxp-input" type="number" value={state.passingScore} onChange={(e) => set("passingScore", Number(e.target.value))} />
        </label>
      </div>

      <h4 style={{ margin: "1rem 0 0.5rem" }}>Questions</h4>
      {state.questions.map((q: Question, i: number) => (
        <div key={q.id} className="lxp-card" style={{ marginBottom: "0.6rem" }}>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
            <select className="lxp-input" value={q.type} onChange={(e) => setQ(i, { type: e.target.value })}>
              <option value="short">Short answer</option>
              <option value="long">Long answer</option>
              <option value="mcq">Multiple choice</option>
              <option value="boolean">True / False</option>
            </select>
            <input className="lxp-input" style={{ maxWidth: 100 }} type="number" value={q.points} onChange={(e) => setQ(i, { points: Number(e.target.value) })} placeholder="Points" />
            <button className="lxp-btn-ghost" onClick={() => setState((s: any) => ({ ...s, questions: s.questions.filter((_: any, j: number) => j !== i) }))} aria-label="Remove"><Trash2 size={14} /></button>
          </div>
          <textarea className="lxp-textarea" rows={2} placeholder="Question prompt" value={q.prompt} onChange={(e) => setQ(i, { prompt: e.target.value })} />
          {q.type === "mcq" && (
            <div style={{ marginTop: "0.5rem" }}>
              <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Options (one per line) — put the correct option in "Correct answer"</div>
              <textarea className="lxp-textarea" rows={3} value={(q.options || []).join("\n")} onChange={(e) => setQ(i, { options: e.target.value.split("\n").filter(Boolean) })} />
              <input className="lxp-input" style={{ marginTop: "0.4rem" }} placeholder="Correct answer (must match one option)" value={(q.answer as string) || ""} onChange={(e) => setQ(i, { answer: e.target.value })} />
            </div>
          )}
          {q.type === "boolean" && (
            <select className="lxp-input" style={{ marginTop: "0.5rem" }} value={String(q.answer ?? "true")} onChange={(e) => setQ(i, { answer: e.target.value })}>
              <option value="true">Correct: True</option>
              <option value="false">Correct: False</option>
            </select>
          )}
        </div>
      ))}
      <button className="lxp-btn-ghost" onClick={() => setState((s: any) => ({ ...s, questions: [...s.questions, emptyQuestion()] }))}>
        <Plus size={14} /> Add question
      </button>

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.25rem", justifyContent: "flex-end" }}>
        <button className="lxp-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="lxp-btn" onClick={() => onSave(state)}><Save size={14} /> Save template</button>
      </div>
    </Modal>
  );
}

function SendDialog({ templateTitle, onCancel, onSend }: { templateTitle: string; onCancel: () => void; onSend: (p: { candidateId: string; message?: string; dueAt?: string }) => void }) {
  const [candidateId, setCandidateId] = useState("");
  const [message, setMessage] = useState("");
  const [dueAt, setDueAt] = useState("");
  return (
    <Modal onClose={onCancel}>
      <h3 style={{ margin: "0 0 1rem" }}>Send "{templateTitle}"</h3>
      <label style={{ display: "block", marginBottom: "0.6rem" }}>
        <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Candidate user ID</div>
        <input className="lxp-input" placeholder="UUID from candidate search" value={candidateId} onChange={(e) => setCandidateId(e.target.value)} />
      </label>
      <label style={{ display: "block", marginBottom: "0.6rem" }}>
        <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Message (optional)</div>
        <textarea className="lxp-textarea" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
      </label>
      <label style={{ display: "block", marginBottom: "0.6rem" }}>
        <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Due date (optional)</div>
        <input className="lxp-input" type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
      </label>
      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
        <button className="lxp-btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="lxp-btn" disabled={!candidateId} onClick={() => onSend({
          candidateId, message: message || undefined,
          dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
        })}><Send size={14} /> Send</button>
      </div>
    </Modal>
  );
}

function GradeDialog({ initial, onCancel, onSave }: { initial: { score: number; feedback: string }; onCancel: () => void; onSave: (p: { score: number; feedback: string }) => void }) {
  const [score, setScore] = useState(initial.score);
  const [feedback, setFeedback] = useState(initial.feedback);
  return (
    <Modal onClose={onCancel}>
      <h3 style={{ margin: "0 0 1rem" }}>Grade submission</h3>
      <label style={{ display: "block", marginBottom: "0.6rem" }}>
        <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Score (0-100)</div>
        <input className="lxp-input" type="number" min={0} max={100} value={score} onChange={(e) => setScore(Number(e.target.value))} />
      </label>
      <label style={{ display: "block", marginBottom: "0.6rem" }}>
        <div className="lxp-muted" style={{ fontSize: "0.75rem", marginBottom: 4 }}>Feedback</div>
        <textarea className="lxp-textarea" rows={5} value={feedback} onChange={(e) => setFeedback(e.target.value)} />
      </label>
      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
        <button className="lxp-btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="lxp-btn" onClick={() => onSave({ score, feedback })}><CheckCircle2 size={14} /> Save grade</button>
      </div>
    </Modal>
  );
}
